import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { IPaginatedResponse } from "../models/Pagination";
import {
  IContact,
  IContactQuery,
  ICreateContact,
  ICreateCustomer,
  ICustomer,
  ICustomerQuery,
  IUpdateCustomer,
} from "../models/Sale";
import { saleService } from "../services/saleService";

export function useGetCustomer(
  query: ICustomerQuery,
): UseQueryResult<IPaginatedResponse<ICustomer>> {
  return useQuery({
    queryKey: ["customer", query],
    queryFn: () => saleService.getAllCustomer(query),
  });
}
export function useUpdateCustomer(): UseMutationResult<
  ICustomer,
  unknown,
  { id: number; data: IUpdateCustomer; query: ICustomerQuery },
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => saleService.updateCustomer(id, data),
    onSuccess: (data, variables) => {
      queryClient.setQueriesData(
        {
          queryKey: ["customer", variables.query],
        },
        (oldData: IPaginatedResponse<ICustomer>) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            data: oldData.data.map((item: ICustomer) =>
              item.id === data.id ? data : item,
            ),
          };
        },
      );
    },
  });
}
export function useCreateCustomer(): UseMutationResult<
  ICustomer,
  unknown,
  ICreateCustomer,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => saleService.createCustomer(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["customer"],
        predicate: (query) => query.queryKey[0] === "customer",
      });
    },
  });
}
export function useDeleteCustomer(): UseMutationResult<
  ICustomer,
  unknown,
  { id: number; query: ICustomerQuery },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => saleService.deleteCustomer(id),
    onSuccess: (_, variables) => {
      queryClient.setQueriesData(
        {
          queryKey: ["customer", variables.query],
        },
        (oldData: IPaginatedResponse<ICustomer>) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            data: oldData.data.filter((item) => item.id !== variables.id),
          };
        },
      );
    },
  });
}
export function useGetContact(
  query: IContactQuery,
): UseQueryResult<IPaginatedResponse<IContact>> {
  return useQuery({
    queryKey: ["contact", query],
    queryFn: () => saleService.getAllContact(query),
  });
}
export function useCreateContact(): UseMutationResult<
  IContact,
  unknown,
  ICreateContact,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => saleService.createContact(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["contact"],
        predicate: (query) => query.queryKey[0] === "contact",
      });
    },
  });
}
export function useUpdateContact(): UseMutationResult<
  IContact,
  unknown,
  { id: number; data: ICreateContact; query: IContactQuery },
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => saleService.updateContact(id, data),
    onSuccess: (data, variables) => {
      queryClient.setQueriesData(
        {
          queryKey: ["contact", variables.query],
        },
        (oldData: IPaginatedResponse<IContact>) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            data: oldData.data.map((item: IContact) =>
              item.id === data.id ? data : item,
            ),
          };
        },
      );
    },
  });
}
export function useDeleteContact(): UseMutationResult<
  IContact,
  unknown,
  { id: number; query: IContactQuery },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => saleService.deleteContact(id),
    onSuccess: (_, variables) => {
      queryClient.setQueriesData(
        {
          queryKey: ["contact", variables.query],
        },
        (oldData: IPaginatedResponse<IContact>) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            data: oldData.data.filter((item) => item.id !== variables.id),
          };
        },
      );
    },
  });
}
